import React, {useCallback, useContext, useEffect, useState} from 'react';
import {Col, Container, Form, FormControl, InputGroup, Modal, Row} from "react-bootstrap";
import CreatableSelect from "react-select/creatable";
import {VALIDATION_IN_JS} from "utils/Constants";
import {CLEARING_SYSTEM_UPDATE_URL, COUNTRY_LIST_COUNTRY_NAME} from "service/UrlConstant";
import {SecurityContext} from "context/security";
import {ToastContext} from "context/Toast";
import {USABA_CLEARING_SYSTEM_CODE, USABA_CLEARING_SYSTEM_ID_PREFIX} from "pages/admin/wiretemplate/AddNewWireTemplateStep1";
import useClearingSystemCodes from "./hooks/useClearingSystemCodes";

const initEditState = {
    clearingSystemCode: '',
    clearingSystemId: '',
    agentName: '',
    agentCity: '',
    agentProvince: '',
    agentCountry: ''
}

// USABA clearing system id rules (mirrors ClearingSystemCreatePanel.js):
// the id must start with the FW prefix followed by digits only.
const USABA_CLEARING_SYSTEM_ID_FORMAT_ERROR =
    `Clearing System ID must start with ${USABA_CLEARING_SYSTEM_ID_PREFIX} followed by digits`;

const isUsabaCode = (code) =>
    (code || '').toString().trim().toUpperCase() === USABA_CLEARING_SYSTEM_CODE;

const isValidUsabaClearingSystemId = (id) =>
    new RegExp(`^${USABA_CLEARING_SYSTEM_ID_PREFIX}\\d+$`).test((id || '').toString().trim());

// Force the FW prefix and keep only the digits the user types after it.
const formatUsabaClearingSystemId = (value) => {
    let raw = (value || '').toString();
    if (raw.length < USABA_CLEARING_SYSTEM_ID_PREFIX.length) {
        raw = USABA_CLEARING_SYSTEM_ID_PREFIX
    }
    const afterPrefix = raw.toUpperCase().startsWith(USABA_CLEARING_SYSTEM_ID_PREFIX)
                                   ? raw.slice(USABA_CLEARING_SYSTEM_ID_PREFIX.length)
                                   : raw

    return `${USABA_CLEARING_SYSTEM_ID_PREFIX}${afterPrefix}`;
}

export default function ClearingSystemEditModal(props) {

    const {show, setShow, selectedRecord, handleRefresh} = props;
    const [editRecord, setEditRecord] = useState({...initEditState});
    const [formErrors, setFormErrors] = useState({});
    const [countries, setCountries] = useState([]);
    const {getClearingSystemCodeList, clearingSystemCodeOptions} = useClearingSystemCodes(editRecord.clearingSystemCode);
    const {makePostRequest, makeGetRequestNoParams} = useContext(SecurityContext);
    const {error, success} = useContext(ToastContext);

    const getCountries = useCallback(() => {
        const onSuccessResponse = (res) => {
            if (res.data) {
                setCountries(res.data);
            }
        }

        const onErrorResponse = (err) => {
            console.log("Error on get unique iso country code country name: ", err);
        }

        makeGetRequestNoParams(COUNTRY_LIST_COUNTRY_NAME, onSuccessResponse, onErrorResponse)
    }, [makeGetRequestNoParams])

    useEffect(() => {
        getCountries()
        getClearingSystemCodeList()
    }, [getCountries, getClearingSystemCodeList])


    useEffect(() => {
        if (selectedRecord) {
            setEditRecord({
                clearingSystemCode: selectedRecord.clearingSystemCode || '',
                clearingSystemId: selectedRecord.clearingSystemId || '',
                agentName: selectedRecord.agentName || '',
                agentCity: selectedRecord.agentCity || '',
                agentProvince: selectedRecord.agentProvince || '',
                agentCountry: selectedRecord.agentCountry || ''
            });
        }
    }, [selectedRecord])

    const handleClose = () => {
        setShow(false);
        setFormErrors({});
    }

    const handleOnChange = (e) => {
        const {name, value} = e.target;
        setEditRecord(prevState => {
            const nextState = {...prevState, [name]: value};

            if (name === 'clearingSystemCode') {
                // When the code is USABA, the Clearing System ID is prefixed with FW
                // and only digits can follow. Adjust the ID whenever the code changes.
                if (isUsabaCode(value)) {
                    nextState.clearingSystemId = formatUsabaClearingSystemId(prevState.clearingSystemId);
                } else if (isUsabaCode(prevState.clearingSystemCode)) {
                    // Leaving USABA: drop the forced FW prefix
                    nextState.clearingSystemId = '';
                }
            } else if (name === 'clearingSystemId' && isUsabaCode(nextState.clearingSystemCode)) {
                nextState.clearingSystemId = formatUsabaClearingSystemId(value);
            }

            return nextState;
        });
        setFormErrors(prevFormErrors => {
            const {[name]: _, ...otherFormErrors} = prevFormErrors;
            return otherFormErrors;
        })
    }

    const validateSubmission = (record) => {
        let errors = {};
        if (!record.clearingSystemCode || !record.clearingSystemCode.trim())
            errors['clearingSystemCode'] = 'Please input Clearing System Code';
        else if (record.clearingSystemCode.trim().length > 50)
            errors['clearingSystemCode'] = 'Please limit your input to 50 characters or fewer';

        if (!record.clearingSystemId || !record.clearingSystemId.trim())
            errors['clearingSystemId'] = 'Please input Clearing System ID';
        else if (record.clearingSystemId.trim().length > 50)
            errors['clearingSystemId'] = 'Please limit your input to 50 characters or fewer';
        else if (isUsabaCode(record.clearingSystemCode) && !isValidUsabaClearingSystemId(record.clearingSystemId))
            errors['clearingSystemId'] = USABA_CLEARING_SYSTEM_ID_FORMAT_ERROR;

        if (!record.agentName || !record.agentName.trim())
            errors['agentName'] = 'Please input Agent Name';
        else if (record.agentName.trim().length > 255)
            errors['agentName'] = 'Please limit your input to 255 characters or fewer';

        if (!record.agentCity || !record.agentCity.trim())
            errors['agentCity'] = 'Please input Agent City';
        else if (record.agentCity.trim().length > 35)
            errors['agentCity'] = 'Please limit your input to 35 characters or fewer';

        if (!record.agentProvince || !record.agentProvince.trim())
            errors['agentProvince'] = 'Please input Agent Province';
        else if (record.agentProvince.trim().length > 35)
            errors['agentProvince'] = 'Please limit your input to 35 characters or fewer';

        if (!record.agentCountry || !record.agentCountry.trim())
            errors['agentCountry'] = 'Please select Agent Country';
        else if (record.agentCountry.trim().length > 2)
            errors['agentCountry'] = 'Please use the 2 character country code';

        return errors;
    }

    const editHandler = (e) => {
        e.preventDefault();
        let errors = {};
        if (VALIDATION_IN_JS) {
            errors = validateSubmission(editRecord);
        }
        setFormErrors(errors);
        if (Object.keys(errors).length !== 0)
            return;

        const onSuccess = () => {
            setShow(false);
            if (success) success("Clearing system updated successfully");
            if (handleRefresh) handleRefresh();
        }
        const onError = (err) => {
            console.error(err);
            if (error) error("Error updating clearing system");
        }

        const editParams = {
            id: selectedRecord.id,
            clearingSystemCode: editRecord.clearingSystemCode,
            clearingSystemId: editRecord.clearingSystemId,
            agentName: editRecord.agentName || null,
            agentCity: editRecord.agentCity || null,
            agentProvince: editRecord.agentProvince || null,
            agentCountry: editRecord.agentCountry || null
        }

        makePostRequest(editParams, CLEARING_SYSTEM_UPDATE_URL, onSuccess, onError);
    }

    const renderField = (label, name, required, maxLength) => (
        <Col xs={12} sm={12} md={12} className={'py-0'}>
            <Form.Group controlId={`formClearingSystem-${name}`}>
                <label className='my-0'>{label}{required && <span className='red-cell'> *</span>}</label>
                <InputGroup>
                    <FormControl
                        className='pl-3 rounded'
                        type="text"
                        maxLength={maxLength}
                        value={editRecord[name]}
                        name={name}
                        onChange={handleOnChange}
                        isInvalid={!!formErrors[name]}
                    />
                    <Form.Control.Feedback type={"invalid"}>
                        <b><i className="bi bi-exclamation-circle"></i>{formErrors[name]}</b>
                    </Form.Control.Feedback>
                </InputGroup>
            </Form.Group>
        </Col>
    );

    // Clearing System Code is an editable combobox: the user can either pick an
    // existing code from the dropdown or type a brand-new code.
    const renderClearingSystemCodeField = (label, name, required) => {
        const selectedValue = editRecord[name]
            ? {label: editRecord[name], value: editRecord[name]}
            : null;
        return (
            <Col xs={12} sm={12} md={12} className={'py-0'}>
                <Form.Group controlId={`formClearingSystem-${name}`}>
                    <label className='my-0'>{label}{required && <span className='red-cell'> *</span>}</label>
                    <CreatableSelect
                        isClearable
                        name={name}
                        inputId={`formClearingSystem-${name}`}
                        options={clearingSystemCodeOptions}
                        value={selectedValue}
                        onChange={(option) =>
                            handleOnChange({target: {name, value: option ? option.value : ''}})}
                        placeholder="Select or enter code"
                        formatCreateLabel={(input) => `Use "${input}"`}
                        maxMenuHeight={200}
                    />
                    {formErrors[name] &&
                    <div className="invalid-feedback d-block">
                        <b><i className="bi bi-exclamation-circle"></i>{formErrors[name]}</b>
                    </div>}
                </Form.Group>
            </Col>
        );
    };

    const renderCountryField = (label, name, required) => (
        <Col xs={12} sm={12} md={12} className={'py-0'}>
            <Form.Group controlId={`formClearingSystem-${name}`} className="td-select">
                <label className='my-0'>{label}{required && <span className='red-cell'> *</span>}</label>
                <InputGroup>
                    <FormControl
                        className='pl-3 rounded'
                        as="select"
                        value={editRecord[name]}
                        name={name}
                        onChange={handleOnChange}
                        isInvalid={!!formErrors[name]}
                    >
                        <option value={''}>Select</option>
                        {countries.map(country => (
                            <option key={country.countryCode} value={country.countryCode}>{country.country}</option>
                        ))}
                    </FormControl>
                    <Form.Control.Feedback type={"invalid"}>
                        <b><i className="bi bi-exclamation-circle"></i>{formErrors[name]}</b>
                    </Form.Control.Feedback>
                </InputGroup>
            </Form.Group>
        </Col>
    );

    return (<div className='p-8'>
        <Modal
            show={show}
            onHide={handleClose}
            animation={false}
            className='pb-0' id='clearing-system-edit-modal'
            backdrop='static'
        >
            <Modal.Header closeButton
                          style={{textAlign: 'right', border: 'none', alignSelf: 'flex-end'}} className='pb-0'>
            </Modal.Header>
            <Container className="mt-md-3 mt-sm-0 pb-4 pt-0">
                <Form className={'mt-0'} onSubmit={editHandler}>
                    <Col>
                        <h2 className='mb-4'>Edit Clearing System</h2>
                    </Col>
                    {renderClearingSystemCodeField('Clearing System Code', 'clearingSystemCode', true)}
                    {renderField('Clearing System ID', 'clearingSystemId', true, 50)}
                    {renderField('Agent Name', 'agentName', true, 255)}
                    {renderField('Agent City', 'agentCity', true, 35)}
                    {renderField('Agent Province', 'agentProvince', true, 35)}
                    {renderCountryField('Agent Country', 'agentCountry', true)}
                    <Col>
                        <Row className='pr-5 pl-5'>
                            <Col md={6}>
                                <button type="button" className="btn btn-block td-btn-secondary-clear my-3"
                                        onClick={handleClose}>Cancel
                                </button>
                            </Col>
                            <Col md={6}>
                                <button type="submit" className="btn btn-block td-btn-primary-light my-3">Update</button>
                            </Col>
                        </Row>
                    </Col>
                </Form>
            </Container>
        </Modal>
    </div>);
}

